import { Step } from 'react-joyride';

/**
 * Tour: Matters Welcome
 * Shown when a user first visits /matters with an empty list.
 * Highlights the key areas of the page so the user knows how to get started.
 */
export const mattersWelcomeTourSteps: Step[] = [
  {
    target: '[data-tour-id="matters-add-button"]',
    title: 'Create Your First Matter',
    content:
      'Start here to create a new matter. You can quickly set one up with just a name, or go through a guided flow with document analysis.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="matters-search-bar"]',
    title: 'Search & Filter',
    content:
      'As your matters grow, use the search bar and filters to quickly find what you need. Filter by stage, status, or sort by date.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="matters-view-toggle"]',
    title: 'Choose Your View',
    content:
      'Switch between a table view for detailed lists or a card view for a visual overview of your matters.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

/**
 * Tour: Matters Overview
 * Shown when the user has matters and visits /matters for the first time.
 * Walks through the key areas of the list page.
 */
export const mattersOverviewTourSteps: Step[] = [
  {
    target: '[data-tour-id="matters-add-button"]',
    title: 'Create a New Matter',
    content:
      'Click here to start a new matter. Choose Quick Create for a fast setup, or Create & Analyze to upload documents and run instant legal research.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="matters-search-bar"]',
    title: 'Find Your Matters',
    content:
      'Search by name, or use filters to narrow by stage and status. You can also sort by date or name to find what you need quickly.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="matters-table"] thead',
    title: 'Your Matters at a Glance',
    content:
      'Each column shows key details - name, stage, status, creation date, and who is involved. Click any column header to sort your list.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="matters-table"] tbody tr:first-child',
    title: 'Matter Details',
    content:
      'The flag icon shows the jurisdiction, and the label beneath the name indicates the category (e.g. Contract, Real Estate). Click any row to open its full detail page with documents, research, and analysis.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="matters-actions"]',
    title: 'Bulk Actions',
    content:
      'Select matters using the checkboxes, then use this Actions menu for bulk operations like exporting or deleting. Each row also has a three-dot menu for individual actions.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

/**
 * Tour: Matter Intent Selection
 * Shown when the user first opens the creation form and sees Quick Create vs. Create & Analyze.
 */
export const matterIntentTourSteps: Step[] = [
  {
    target: '[data-tour-id="intent-quick-create"]',
    title: 'Quick Create',
    content:
      'Create a matter instantly with just a name. Ideal when you want to set up a shell and add documents, clients, or analysis later.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="intent-create-analyze"]',
    title: 'Create & Analyze',
    content:
      'Upload documents and let PONS run legal research, generate insights, and suggest next steps - all in a guided setup.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

/**
 * Tour: Matter Wizard Steps
 * Shown during the first run of the Create & Analyze wizard.
 * Each step tooltip appears when that wizard phase becomes active.
 * These are used as contextual tip data rather than a Joyride tour.
 */
export const matterWizardStepTips: Record<
  string,
  { title: string; content: string }
> = {
  basics: {
    title: 'Start with the Essentials',
    content:
      'Name your matter clearly - this is how you will find it later. The jurisdiction and language determine which legal databases PONS searches.',
  },
  documents: {
    title: 'Add Your Documents',
    content:
      'Upload the key documents for PONS to analyze - contracts, pleadings, correspondence. Or describe the situation in your own words instead.',
  },
  research: {
    title: 'Pick Your Sources',
    content:
      'These are the legal databases PONS will search. Defaults are selected based on your jurisdiction. You can also connect your own knowledge bases.',
  },
  review: {
    title: 'Review Before Launch',
    content:
      'All features are enabled by default. Toggle off anything you do not need right now - you can always run them later from the matter page.',
  },
};

/**
 * Contextual tip shown on the intent selection screen.
 */
export const matterIntentTip = {
  title: 'Choose Your Path',
  content:
    'Quick Create sets up a matter shell in seconds - add documents and run analysis anytime. Create & Analyze walks you through a guided setup with immediate research results.',
};

/**
 * Contextual tip shown on the Quick Create form.
 */
export const matterQuickCreateTip = {
  title: 'Fast Setup, Full Flexibility',
  content:
    'Just give your matter a name to get started. You can attach files, assign clients, add collaborators, and run PONS analysis later from the matter page.',
};

/**
 * Tour: Create & Analyze Wizard - per-step Joyride tours.
 * Each wizard step has its own set of spotlight steps that highlight
 * the key form elements on that step so the user knows what to do.
 *
 * Targets use [id="fieldName"] (set by BaseForm) and data-tour-id attributes.
 */
export const matterWizardTourSteps: Record<string, Step[]> = {
  basics: [
    {
      target: '[data-tour-id="wizard-step-indicator"]',
      title: 'Your Guided Journey',
      content:
        'These four steps walk you through the full setup. You can go back to any step before launching.',
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour-id="field-matterTitle"]',
      title: 'Name Your Matter',
      content:
        'Choose a clear, descriptive name. This is how you will find it later in your matters list.',
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour-id="field-jurisdiction"]',
      title: 'Set Jurisdiction & Language',
      content:
        'These determine which legal databases PONS searches. Pick the jurisdiction that applies to your matter.',
      placement: 'top',
      disableBeacon: true,
    },
  ],

  documents: [
    {
      target: '[data-tour-id="field-filesOrDescription"]',
      title: 'Provide Your Materials',
      content:
        'Upload documents like contracts, pleadings, or correspondence for PONS to analyze. You can also describe the situation in your own words instead.',
      placement: 'top',
      disableBeacon: true,
    },
  ],

  research: [
    {
      target: '[data-tour-id="field-selectedIndexes"]',
      title: 'Legal Databases',
      content:
        'These are official legal databases for your jurisdiction - legislation, case law, preparatory works, and more. Defaults are pre-selected based on your jurisdiction.',
      placement: 'top',
      disableBeacon: true,
    },
    {
      target: '[data-tour-id="field-dataSources"]',
      title: 'Your Knowledge Bases',
      content:
        'Connect your own data sources - internal policies, research archives, or industry standards - so PONS can search them alongside official databases.',
      placement: 'top',
      disableBeacon: true,
    },
  ],

  review: [
    {
      target: '[data-tour-id="review-summary"]',
      title: 'Your Matter at a Glance',
      content:
        'Here is a summary of everything you have set up - title, documents, jurisdiction, and sources. Go back to any step if you need to change something.',
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour-id="review-research"]',
      title: 'Legal Research',
      content:
        'These control which legal research PONS runs - searching legislation, case law, and your connected data sources. All are enabled by default.',
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour-id="review-generation"]',
      title: 'PONS Generation',
      content:
        'Choose what PONS should generate - tasks, questionnaires, notes, drafts, and more. Toggle off anything you do not need right now.',
      placement: 'top',
      disableBeacon: true,
    },
  ],
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MATTER DETAILS PAGE - Orientation + Per-Tab Tours
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/**
 * Tour: Matter Detail Orientation
 * Runs once on first visit to any matter detail page.
 * Auto-switches tabs to give a high-level overview of the whole page.
 * Steps with `data.tab` trigger a tab switch before the step renders.
 */
export const matterDetailOrientationSteps: Step[] = [
  {
    target: '[data-tour-id="matter-status-badges"]',
    title: 'Matter Overview',
    content:
      'Welcome to your matter. This bar shows the current stage, status, jurisdiction, language, and key dates at a glance.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="matter-analyze-button"]',
    title: 'Analyze Your Matter',
    content:
      'Run AI analysis to scan your documents, find relevant laws and case law, and generate Q&As, tasks, and notes. This powers everything on this page.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="matter-collaborators"]',
    title: 'Collaborators',
    content:
      'See who has access to this matter. Press the faces to open the Access pane, where the list lives and the "Manage access" button changes it.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="matter-actions-menu"]',
    title: 'Actions Menu',
    content:
      'Manage collaborators, view the activity log, or delete this matter from here.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="matter-tabs-left"]',
    title: 'Context Tabs',
    content:
      'Details, Documents, Q&As, Tasks, and Notes form your matter\'s knowledge base. The richer the context you provide, the better the AI-generated outputs will be.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="matter-tab-details-content"]',
    title: 'Details',
    content:
      'Your matter\'s description, status, clients, and core information. Edit these fields to keep everything current - they feed directly into AI analysis.',
    placement: 'top',
    disableBeacon: true,
    data: { tab: 'Details' },
  },
  {
    target: '[data-tour-id="matter-tab-documents-content"]',
    title: 'Documents',
    content:
      'Upload your documents here. After analysis, PONS retrieves relevant laws, cases, and regulations - organized into sub-tabs for easy browsing.',
    placement: 'top',
    disableBeacon: true,
    data: { tab: 'Documents' },
  },
  {
    target: '[data-tour-id="matter-tab-tasks-content"]',
    title: 'Tasks',
    content:
      'AI-suggested tasks help you track what needs to be done. Edit suggestions, add your own, or generate more at any time.',
    placement: 'top',
    disableBeacon: true,
    data: { tab: 'Tasks' },
  },
  {
    target: '[data-tour-id="matter-tab-drafts-content"]',
    title: 'Drafts',
    content:
      'Generate legal documents - contracts, pleadings, motions, and more. The AI draws from your full matter context to produce accurate, jurisdiction-aware drafts.',
    placement: 'top',
    disableBeacon: true,
    data: { tab: 'Drafts' },
  },
  {
    target: '[data-tour-id="matter-tab-strategy-content"]',
    title: 'Strategy',
    content:
      'Get AI-powered case analysis, strategic recommendations, and risk assessments. Like Drafts, this draws from your complete matter context.',
    placement: 'top',
    disableBeacon: true,
    data: { tab: 'Strategy' },
  },
];

// ── Per-Tab Tours ────────────────────────────────────────────────
// Each runs independently on first visit to its tab.

/** Details tab deep-dive */
export const matterDetailsTabTourSteps: Step[] = [
  {
    target: '[data-tour-id="details-description-card"]',
    title: 'Matter Description',
    content:
      'View and edit your matter\'s description. Use the Generate button to let AI create a summary from your uploaded documents.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="details-clients-section"]',
    title: 'Clients',
    content:
      'Add clients to your matter and assign roles. Client information is included in the AI context for more relevant outputs.',
    placement: 'top',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="details-status-section"]',
    title: 'Status & Stage',
    content:
      'Track your matter\'s progress by updating its stage and status. These help you organize and filter matters across your dashboard.',
    placement: 'top',
    disableBeacon: true,
  },
];

/** Documents (Sources) tab deep-dive */
export const matterDocumentsTabTourSteps: Step[] = [
  {
    target: '[data-tour-id="sources-sub-tabs"]',
    title: 'Document Categories & Actions',
    content:
      'Your documents are organized into categories: uploaded files, retrieved laws, cases, preparatory works, regulations, and agency decisions. Use the action buttons to upload documents or let AI find relevant legal sources.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

/** Q&As tab deep-dive */
export const matterQAsTabTourSteps: Step[] = [
  {
    target: '[data-tour-id="qas-sub-tabs"]',
    title: 'Questions & Answers',
    content:
      'Questions move through three stages: Unanswered, Answered, and Completed. Only Completed questions are included in the AI context. Add questions manually or generate them from your documents.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

/** Tasks tab deep-dive */
export const matterTasksTabTourSteps: Step[] = [
  {
    target: '[data-tour-id="tasks-sub-tabs"]',
    title: 'Suggestions & Active Tasks',
    content:
      'AI suggests tasks based on your matter analysis. Review, edit, and select the ones you want - then create them as active tasks. Use the action buttons to add tasks manually or generate AI suggestions.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

/** Notes tab deep-dive */
export const matterNotesTabTourSteps: Step[] = [
  {
    target: '[data-tour-id="notes-actions"]',
    title: 'Notes & Metadata',
    content:
      'Add structured notes with different types - text, numbers, dates, or booleans. Use Generate to let AI extract key data points from your documents. All notes feed into the AI context.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

/** Drafts tab deep-dive */
export const matterDraftsTabTourSteps: Step[] = [
  {
    target: '[data-tour-id="drafts-sub-tabs"]',
    title: 'Suggested & Generated Drafts',
    content:
      'AI suggests draft types based on your matter (contracts, pleadings, motions, etc.) with priority rankings. Switch to All Drafts to see everything that\'s been generated.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="drafts-actions"]',
    title: 'Generate Drafts',
    content:
      'Click Generate on any suggestion to create a draft. The AI uses your documents, Q&As, tasks, and notes to produce a complete, jurisdiction-aware document.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

/** Strategy (Insights) tab deep-dive */
export const matterStrategyTabTourSteps: Step[] = [
  {
    target: '[data-tour-id="strategy-sub-tabs"]',
    title: 'Suggested & Generated Insights',
    content:
      'AI recommends insight types for your matter - case analysis, strategies, risk assessments, and more. View all generated insights in the second tab.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="strategy-actions"]',
    title: 'Generate Insights',
    content:
      'Generate strategic insights that draw from your full matter context. Each insight includes detailed analysis, recommendations, and actionable next steps.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Draft Viewer Tour - shown when a user opens a generated draft
// ---------------------------------------------------------------------------
export const draftViewerTourSteps: Step[] = [
  {
    target: '[data-tour-id="draft-viewer-toolbar"]',
    title: 'Document Info',
    content:
      'Here you can see the word count, when the document was last modified, and its finalized status at a glance.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="draft-viewer-rerun"]',
    title: 'Regenerate',
    content:
      'Not satisfied with the result? Click Rerun to regenerate the draft using the latest matter data and any customizations you\'ve applied.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="draft-viewer-versions"]',
    title: 'Version History',
    content:
      'Every time a draft is generated, a new version is saved. Browse previous versions here and restore any earlier version if needed.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="draft-viewer-menu"]',
    title: 'More Actions',
    content:
      'Open this menu to customize how PONS generates the draft, finalize it to your Files folder, or download it as DOCX or PDF.',
    placement: 'bottom-end',
    disableBeacon: true,
  },
];

// Draft Viewer - not generated state
export const draftViewerNotGeneratedTourSteps: Step[] = [
  {
    target: '[data-tour-id="draft-viewer-not-generated"]',
    title: 'Generate or Customize',
    content:
      'This draft hasn\'t been generated yet. Click Generate to let PONS create it using your matter data, or click Customize to fine-tune how it\'s built before generating.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Insight Viewer Tour - shown when a user opens a generated insight
// ---------------------------------------------------------------------------
export const insightViewerTourSteps: Step[] = [
  {
    target: '[data-tour-id="insight-viewer-toolbar"]',
    title: 'Insight Info',
    content:
      'See the word count, last modified date, and finalized status of this insight at a glance.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="insight-viewer-rerun"]',
    title: 'Regenerate',
    content:
      'Click Rerun to regenerate this insight with the latest matter data and any customizations you\'ve set up.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="insight-viewer-versions"]',
    title: 'Version History',
    content:
      'Each generation creates a new version. Browse and restore previous versions here.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="insight-viewer-menu"]',
    title: 'More Actions',
    content:
      'Open this menu to customize how PONS generates the insight, finalize it, or download it as DOCX or PDF.',
    placement: 'bottom-end',
    disableBeacon: true,
  },
];

// Insight Viewer - not generated state
export const insightViewerNotGeneratedTourSteps: Step[] = [
  {
    target: '[data-tour-id="insight-viewer-not-generated"]',
    title: 'Generate or Customize',
    content:
      'This insight hasn\'t been generated yet. Click Generate to let PONS analyze your matter, or click Customize to configure the parameters first.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Customization Panel Tour - shown when the side panel opens
// ---------------------------------------------------------------------------
export const documentCustomizationTourSteps: Step[] = [
  {
    target: '[data-tour-id="customize-info-banner"]',
    title: 'Customization Panel',
    content:
      'This panel lets you control exactly how PONS generates your document. Any changes you make here apply the next time you generate or rerun.',
    placement: 'left',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="customize-instructions"]',
    title: 'Custom Instructions',
    content:
      'Write specific directions for PONS - what to include, exclude, how to structure the content, or any special requirements.',
    placement: 'left',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="customize-tone"]',
    title: 'Writing Tone',
    content:
      'Choose a tone that matches your audience - formal for court filings, persuasive for arguments, concise for summaries, and more.',
    placement: 'left',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="customize-context-sources"]',
    title: 'Context Sources',
    content:
      'Toggle which matter data PONS uses when generating. Include or exclude laws, cases, documents, notes, tasks, and more to focus the output. Scroll down to see all available sources.',
    placement: 'left',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="customize-focus-areas"]',
    title: 'Focus Areas',
    content:
      'Add specific topics for PONS to emphasize - like "liability analysis" or "damages calculation". These help guide the AI\'s attention.',
    placement: 'left',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="customize-attachments"]',
    title: 'Reference Attachments',
    content:
      'Upload reference documents (DOCX, PDF, TXT) that PONS should consider when generating. Great for templates or precedent documents.',
    placement: 'left',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="customize-footer"]',
    title: 'Save Your Settings',
    content:
      'Click Save to apply your customizations. They\'ll be used the next time this document is generated or rerun. You can also reset everything back to defaults.',
    placement: 'top-end',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Source Reader Tour - shown when the reader side panel opens
// ---------------------------------------------------------------------------
export const sourceReaderTourSteps: Step[] = [
  {
    target: '[data-tour-id="reader-sidebar"]',
    title: 'Source Overview',
    content:
      'This sidebar shows metadata, AI-generated summary, relevant sections, and justification for why this source is relevant to your matter.',
    placement: 'right',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="reader-content"]',
    title: 'Document Viewer',
    content:
      'The full document is displayed here - PDFs, HTML content, Word documents, and more. Scroll through to review the original source material.',
    placement: 'left',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="reader-actions"]',
    title: 'Source Actions',
    content:
      'Remove this source from your matter if it\'s no longer relevant. You can always find and add new sources from the Documents tab.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Questionnaire Panel Tour - shown when the Q&A side panel opens
// ---------------------------------------------------------------------------
export const questionnairePanelTourSteps: Step[] = [
  {
    target: '[data-tour-id="qa-panel-status"]',
    title: 'Question Status',
    content:
      'Each question moves through stages: Pending → Answered → Completed. The color-coded header shows the current status at a glance.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="qa-panel-question"]',
    title: 'Question',
    content:
      'Review or edit the question text. Questions can be AI-generated or manually added. Once completed, the question is locked.',
    placement: 'left',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="qa-panel-answer"]',
    title: 'Answer',
    content:
      'Provide your answer here. AI may pre-fill a suggested answer, but you can edit it freely. The answer becomes part of the matter context once completed.',
    placement: 'left',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="qa-panel-actions"]',
    title: 'Actions',
    content:
      'Save your changes, mark the question as complete, or delete it. Completed questions can be restored if you need to make further edits.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Sources Items Tour - shown when the Sources tab has items
// ---------------------------------------------------------------------------
export const sourcesItemsTourSteps: Step[] = [
  {
    target: '[data-tour-id="sources-table-area"]',
    title: 'Your Sources',
    content:
      'Each source is listed with its name, type, category, and relevance score. Click any row to open it in the reader panel and see the full document alongside AI analysis.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Q&As Items Tour - shown when the Q&As tab has items
// ---------------------------------------------------------------------------
export const qasItemsTourSteps: Step[] = [
  {
    target: '[data-tour-id="qas-table-area"]',
    title: 'Your Questions',
    content:
      'Questions are listed with their current answer and status. Click any row to open the question panel where you can edit answers and manage status.',
    placement: 'top',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="qas-context-hint"]',
    title: 'Context Tip',
    content:
      'Only completed questions are included when generating drafts and insights. Make sure to complete questions once you\'re satisfied with the answer.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Notes Items Tour - shown when the Notes tab has items
// ---------------------------------------------------------------------------
export const notesItemsTourSteps: Step[] = [
  {
    target: '[data-tour-id="notes-table-area"]',
    title: 'Your Notes',
    content:
      'Notes are organized with labels, values, and types (text, number, date, yes/no). Click any row to edit it, or use the action menu for quick edits and deletion.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Tasks Items Tour - shown when the Tasks tab has items
// ---------------------------------------------------------------------------
export const tasksItemsTourSteps: Step[] = [
  {
    target: '[data-tour-id="tasks-content-area"]',
    title: 'Your Tasks',
    content:
      'AI-suggested tasks can be reviewed, edited, and selected here. Adjust titles, descriptions, priorities, and deadlines - then generate the ones you want to keep as active tasks.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ─── Analyze Wizard Tour ──────────────────────────────────────────────

export const analyzeWizardTourSteps: Step[] = [
  {
    target: '[data-tour-id="analyze-wizard-steps"]',
    title: 'Analysis Wizard',
    content:
      'This 3-step wizard configures your analysis. Choose what to research, select your sources, then review and launch.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="analyze-wizard-content"]',
    title: 'Configure Options',
    content:
      'Toggle individual research and generation features on or off. Each step lets you fine-tune exactly what AI will do for your matter.',
    placement: 'top',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="analyze-wizard-footer"]',
    title: 'Navigate & Launch',
    content:
      'Step through the wizard with Next and Back, then hit Launch to kick off the analysis with your chosen settings.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// REVIEW FEATURE - Tours for the Contract Review Workflow
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// ---------------------------------------------------------------------------
// Review List Tour - shown when user first visits /reviews
// ---------------------------------------------------------------------------
export const reviewListTourSteps: Step[] = [
  {
    target: '[data-tour-id="review-list-new-button"]',
    title: 'Start a New Review',
    content:
      'Click here to start a contract review. You\'ll choose a playbook and upload the documents you want to analyze.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="review-list-filter-bar"]',
    title: 'Search & Filter',
    content:
      'Use the search bar to find reviews by name, or filter by status - Processing, In Review, Awaiting Sign-Off, or Finalized.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="review-list-table"]',
    title: 'Your Reviews',
    content:
      'Each row shows a review session with its playbook, findings count, risk score, and current status. Click any row to open the full review workspace.',
    placement: 'bottom-start',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Playbook Detail Tour - shown when user first opens a playbook
// ---------------------------------------------------------------------------
export const playbookDetailTourSteps: Step[] = [
  {
    target: '[data-tour-id="playbook-tab-bar"]',
    title: 'Playbook Sections',
    content:
      'A playbook has four sections - Details for metadata, Clause Rules and Global Rules for your review standards, and Reviews to see past sessions.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="playbook-details-section"]',
    title: 'Playbook Details',
    content:
      'Name and describe your playbook. Add agreement types and jurisdictions so PONS knows when to apply these rules.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="playbook-source-document"]',
    title: 'Source Document',
    content:
      'Upload your firm\'s standards document and PONS will automatically extract clause rules and global rules - saving you hours of manual setup.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Clause Rules Tour - shown when user first visits the clause rules tab
// ---------------------------------------------------------------------------
export const clauseRulesTourSteps: Step[] = [
  {
    target: '[data-tour-id="clause-rules-header"]',
    title: 'Clause Rules',
    content:
      'Clause rules define specific contract clauses the AI should check - like non-compete, indemnification, or liability limitation.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="clause-rules-add-button"]',
    title: 'Add a Rule',
    content:
      'Click here to define a new clause rule. Set the clause type, your firm\'s preferred starting position, red lines, acceptable variations, and risk weight.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="clause-rules-list"]',
    title: 'Manage Your Rules',
    content:
      'Each rule card shows its clause type, risk weight, and tags. Hover over a rule to reveal the edit (pencil) and delete buttons - click edit to update any field like starting position, red lines, or acceptable variations.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Global Rules Tour - shown when user first visits the global rules tab
// ---------------------------------------------------------------------------
export const globalRulesTourSteps: Step[] = [
  {
    target: '[data-tour-id="global-rules-header"]',
    title: 'Global Rules',
    content:
      'Global rules are cross-cutting standards that apply to every clause in a contract - like language requirements, governing law, or formatting standards.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="global-rules-add-button"]',
    title: 'Add a Global Rule',
    content:
      'Define a rule with a description and severity level. Critical and major rules flag high-priority issues during review.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="global-rules-list"]',
    title: 'Manage Your Rules',
    content:
      'Your global rules are listed here with their severity badges. Hover over any rule to edit its text or severity, or delete it entirely.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Review Session Detail Tour - the main review workspace
// ---------------------------------------------------------------------------
export const reviewSessionTourSteps: Step[] = [
  {
    // The summary bar this used to point at is gone; its numbers moved into
    // the page header, so the step follows them rather than pointing at a
    // selector that no longer matches anything.
    target: '[data-tour-id="review-header-readout"]',
    title: 'Review Overview',
    content:
      'Your at-a-glance readout - the overall risk score, the findings breakdown by severity, and how far through them you are.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="review-findings-panel"]',
    title: 'Findings List',
    content:
      'All AI-detected findings are listed here by severity. Click any finding to see its full details in the center panel.',
    placement: 'right',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="review-findings-filter-bar"]',
    title: 'Filter Findings',
    content:
      'Narrow the list by severity (Critical, Major, Minor, Info) or by decision status (Pending, Accepted, Rejected, Modified, Escalated).',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="review-finding-detail"]',
    title: 'Finding Detail',
    content:
      'Review each finding in depth - see the original text, the issue, suggested redline or addition, and the legal basis for the flag.',
    placement: 'left',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="review-finding-decisions"]',
    title: 'Make Your Decision',
    content:
      'For each finding, Accept the suggested change, Reject it, Modify the text yourself, or Escalate it for team review. Add optional notes to explain your reasoning.',
    placement: 'top',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="review-session-view-doc"]',
    title: 'View Document',
    content:
      'Open the original document to see findings in context. Switch between the original and revised (clean or redline) versions.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="review-session-activity"]',
    title: 'Activity Log',
    content:
      'Track every action taken during this review - decisions, sign-offs, and status changes. Great for audit trails and team coordination.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Document Viewer Tour - shown when the document viewer modal opens
// ---------------------------------------------------------------------------
export const documentViewerTourSteps: Step[] = [
  {
    target: '[data-tour-id="doc-viewer-toolbar"]',
    title: 'Document Toolbar',
    content:
      'See file metadata and switch between the original document and revised versions. Use Clean for a final-looking doc, or Redline to see tracked changes.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="doc-viewer-content"]',
    title: 'Document Preview',
    content:
      'The full document is rendered here - PDFs, Word documents, and more. Click a finding in the sidebar to jump to its location in the document.',
    placement: 'left',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="doc-viewer-findings"]',
    title: 'Findings Sidebar',
    content:
      'All findings for this document are listed here with severity indicators and decision status. Click any finding to scroll to its location in the document.',
    placement: 'left',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Start Review Form Tour - shown when user navigates to /reviews?add=true
// ---------------------------------------------------------------------------
export const startReviewFormTourSteps: Step[] = [
  {
    target: '[data-tour-id="start-review-name"]',
    title: 'Name Your Review',
    content:
      'Give your review a name so you can easily find it later - this is optional but helpful when running multiple reviews.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="start-review-collaborators"]',
    title: 'Add Collaborators',
    content:
      'Invite team members to collaborate on this review. They can view findings, make decisions, and participate in sign-offs.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="start-review-playbook"]',
    title: 'Choose a Playbook',
    content:
      'Select the playbook that defines your review standards - clause rules, global rules, and risk thresholds the AI will check against.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="start-review-documents"]',
    title: 'Upload Documents',
    content:
      'Upload the contracts or agreements you want reviewed. Each document will be analyzed against every rule in the playbook.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="start-review-language"]',
    title: 'Set Output Language',
    content:
      'Choose the language for AI-generated findings, suggestions, and redlines. Defaults to your AI settings.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// BATCH TABLES TOURS
// ═══════════════════════════════════════════════════════════════════════════

// ── Tables List ──────────────────────────────────────────────────────────────

export const tablesListTourSteps: Step[] = [
  {
    target: '[data-tour-id="tables-list-new-button"]',
    title: 'Create a New Table',
    content:
      'Click here to create a new table. You\'ll define columns (AI queries), upload documents, and optionally start from a template.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="tables-list-filter-bar"]',
    title: 'Search & Filter',
    content:
      'Use the search bar to find tables by name. You can also sort columns and access the recycle bin from here.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="tables-list-table"]',
    title: 'Your Tables',
    content:
      'All your tables are listed here with status, progress, and collaborator info. Click a completed table to open it and inspect results.',
    placement: 'bottom-start',
    disableBeacon: true,
  },
];

// ── Create Table Form ────────────────────────────────────────────────────────

export const createTableFormTourSteps: Step[] = [
  {
    target: '[data-tour-id="create-table-basics"]',
    title: 'Name & Collaborators',
    content:
      'Give your table a descriptive name and optionally share it with team members who can view and comment on results.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="create-table-columns"]',
    title: 'Define Columns',
    content:
      'Each column is an AI query applied to every document. Pick a template for common setups, or define custom columns with a name, type, and query.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="create-table-documents"]',
    title: 'Upload Documents',
    content:
      'Upload the files you want to analyze. Supported formats include PDF, Word, Excel, TXT, and CSV. Processing starts automatically after creation.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ── Table Detail (grid view) ─────────────────────────────────────────────────

export const tableDetailTourSteps: Step[] = [
  {
    target: '[data-tour-id="table-detail-toolbar"]',
    title: 'Table Toolbar',
    content:
      'View the table status, add documents or columns, export results as CSV, and save your column setup as a reusable template.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="table-detail-grid"]',
    title: 'Data Grid',
    content:
      'This spreadsheet-like grid shows every document as a row and every AI query as a column. Click any cell to see the full extracted answer and source.',
    placement: 'bottom-start',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="table-detail-columns"]',
    title: 'Column Headers',
    content:
      'Each column header shows the query name and type. Hover to see the full query, and use the edit or delete buttons to manage columns.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

// ── Cell Detail Panel ────────────────────────────────────────────────────────

export const cellDetailTourSteps: Step[] = [
  {
    target: '[data-tour-id="cell-detail-info"]',
    title: 'Cell Context',
    content:
      'See which document and column this cell belongs to, along with the AI query that was used to extract the value.',
    placement: 'left',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="cell-detail-answer"]',
    title: 'Extracted Answer',
    content:
      'The AI-extracted value is shown here with a confidence indicator. The source excerpt below shows exactly where the answer was found in the document.',
    placement: 'left',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="cell-detail-comments"]',
    title: 'Comments & Collaboration',
    content:
      'Add comments to discuss findings with your team. Use @mentions to notify specific collaborators directly.',
    placement: 'left',
    disableBeacon: true,
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// DATA SOURCES TOURS
// ═══════════════════════════════════════════════════════════════════════════

// ── Data Sources List ────────────────────────────────────────────────────────

export const dataSourcesListTourSteps: Step[] = [
  {
    target: '[data-tour-id="ds-list-header"]',
    title: 'Create a Data Source',
    content:
      'Click "Create Data Source" to start building a new data source. Upload documents and configure sharing to make them available across your workspace.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="ds-list-filter-bar"]',
    title: 'Search & Filter',
    content:
      'Use the search bar to find data sources by name. Filter by type, sort columns, switch between grid and table views, or access the recycle bin.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="ds-list-table"]',
    title: 'Your Data Sources',
    content:
      'All your data sources are listed here with status, document count, collaborators, and creation date. Click a data source to view its details and files.',
    placement: 'bottom-start',
    disableBeacon: true,
  },
];

// ── Create Data Source Form ──────────────────────────────────────────────────

export const createDataSourceFormTourSteps: Step[] = [
  {
    target: '[data-tour-id="ds-create-details"]',
    title: 'Data Source Name',
    content:
      'Give your data source a clear, descriptive name so it\'s easy to find later.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="ds-create-sharing"]',
    title: 'Sharing Settings',
    content:
      'Make the data source available organization-wide, or invite specific team members as collaborators.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="ds-create-documents"]',
    title: 'Upload Documents',
    content:
      'Upload the files to include in this data source. Supported formats are PDF, DOCX, TXT, and MD. Processing starts automatically after creation.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ── Data Source Detail (orientation) ─────────────────────────────────────────

export const dataSourceDetailTourSteps: Step[] = [
  {
    target: '[data-tour-id="ds-detail-header"]',
    title: 'Actions & Collaborators',
    content:
      'Press the faces to open the Access pane, where you can see who this data source is shared with and change it. The menu deletes the data source.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="ds-detail-badges"]',
    title: 'Data Source Overview',
    content:
      'Quick stats showing the data source type, number of files, last processed date, and total storage size.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="ds-detail-tabs"]',
    title: 'Navigate Tabs',
    content:
      'Switch between Details (description & configuration) and Files (manage uploaded documents). Context, Test and Activity open as panels from the toggles on the right.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

// ── Data Source Details Tab ──────────────────────────────────────────────────

export const dataSourceDetailsPanelTourSteps: Step[] = [
  {
    target: '[data-tour-id="ds-details-description"]',
    title: 'Description & Edit',
    content:
      'View and edit the data source name, description, category, and keywords. Click "Edit" to make changes.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="ds-details-metadata"]',
    title: 'Status & Metadata',
    content:
      'Key metrics at a glance - current status, indexer state, document count, and total storage size.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="ds-details-sidebar"]',
    title: 'Keywords & Formats',
    content:
      'View auto-generated keywords and supported file formats. Keywords help improve search relevance across your workspace.',
    placement: 'left',
    disableBeacon: true,
  },
];

// ── Data Source Files Tab ────────────────────────────────────────────────────

export const dataSourceFilesPanelTourSteps: Step[] = [
  {
    target: '[data-tour-id="ds-files-filter-bar"]',
    title: 'Search & Add Files',
    content:
      'Search through uploaded files by name, or click "Add Data" to upload more documents to this data source.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="ds-files-table"]',
    title: 'File List',
    content:
      'All uploaded files are shown here with type, size, and last modified date. Click a file to preview it, or use the actions menu to download or delete.',
    placement: 'bottom-start',
    disableBeacon: true,
  },
];

// ── Data Source Test Tab ─────────────────────────────────────────────────────

export const dataSourceTestTourSteps: Step[] = [
  {
    target: '[data-tour-id="ds-test-chat"]',
    title: 'Chat with Your Data',
    content:
      'Ask questions about the documents in this data source. The AI will search through your files and provide answers based on their content.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="ds-test-input"]',
    title: 'Enter a Prompt',
    content:
      'Type your question here and press Enter or click the send button. You can ask follow-up questions to refine the results.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ═══════════════════════════════════════════════════════════════════════════
//  TEMPLATES
// ═══════════════════════════════════════════════════════════════════════════

// ── Templates List ───────────────────────────────────────────────────────

export const templatesListTourSteps: Step[] = [
  {
    target: '[data-tour-id="templates-list-header"]',
    title: 'Create Templates & Playbooks',
    content:
      'Click "Add Template" or "Add Playbook" to create a new document structure template, table columns template, or review playbook.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="templates-list-tabs"]',
    title: 'Template Categories',
    content:
      'Switch between Document Structure templates, Table Columns templates, and Playbooks. Each tab shows a different type of reusable template.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="templates-list-table"]',
    title: 'Browse Your Templates',
    content:
      'View all templates or playbooks in this list. Click any row to open its detail page, or use the actions menu for quick operations like delete.',
    placement: 'bottom-start',
    disableBeacon: true,
  },
];

// ── Structure Form (Create) ─────────────────────────────────────────────

export const createStructureFormTourSteps: Step[] = [
  {
    target: '[data-tour-id="structure-form-name"]',
    title: 'Template Name & Type',
    content:
      'Give your template a descriptive name and select the document type it applies to (e.g., Contract, NDA, Legal Opinion).',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="structure-form-sharing"]',
    title: 'Sharing Settings',
    content:
      'Make the template available organization-wide, or invite specific collaborators who should have access.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="structure-form-upload"]',
    title: 'Upload a Document',
    content:
      'Upload a sample document (DOCX or PDF) and AI will analyze it to extract the template structure, tone, and writing style automatically.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

// ── Structure Form (Detail / Edit) ──────────────────────────────────────

export const structureDetailTourSteps: Step[] = [
  {
    target: '[data-tour-id="structure-detail-sidebar"]',
    title: 'Outline Navigation',
    content:
      'The left sidebar shows the full document outline. Click any section to jump to it in the editor. Expand or collapse subsections as needed.',
    placement: 'right',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="structure-detail-editor"]',
    title: 'Section Editor',
    content:
      'Edit each section\'s title, content prompt, example language, and fallbacks. Drag and drop sections to reorder them, or add new sections at the bottom.',
    placement: 'left',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="structure-detail-metadata"]',
    title: 'Description & Style',
    content:
      'Click any panel on the right to edit the template description, tone, and writing style. These guide the AI when generating documents from this template.',
    placement: 'left',
    disableBeacon: true,
  },
];

// ── Table Columns Form ──────────────────────────────────────────────────

export const tableColumnsFormTourSteps: Step[] = [
  {
    target: '[data-tour-id="table-cols-details"]',
    title: 'Template Details',
    content:
      'Set the template name, description, and category. The category helps organize templates for easy discovery.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="table-cols-columns"]',
    title: 'Column Definitions',
    content:
      'Define the columns for your batch table. Each column has a name, data type, and an AI query that tells the system what data to extract for that column.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="table-cols-sharing"]',
    title: 'Sharing Settings',
    content:
      'Make this template available to your entire organization, or invite specific collaborators who need access.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

// ── Create Playbook Form ────────────────────────────────────────────────

export const createPlaybookFormTourSteps: Step[] = [
  {
    target: '[data-tour-id="playbook-form-name"]',
    title: 'Playbook Name',
    content:
      'Optionally give your playbook a name. If left blank, the AI will extract the name from the uploaded document automatically.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="playbook-form-upload"]',
    title: 'Upload Playbook Document',
    content:
      'Upload your firm\'s playbook or negotiation guidelines. AI will extract clause rules, global rules, agreement types, and other metadata automatically.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="playbook-form-collaborators"]',
    title: 'Invite Collaborators',
    content:
      'Optionally add team members who should have access to this playbook for contract review sessions.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Playbook Sessions Tour - shown when user visits the Reviews tab in a playbook
// ---------------------------------------------------------------------------
export const playbookSessionsTourSteps: Step[] = [
  {
    target: '[data-tour-id="playbook-sessions-start"]',
    title: 'Run a Review',
    content:
      'Start a contract review using this playbook\'s rules directly from here. Click "Go to Reviews" to upload contracts and run them against your clause and global rules.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="playbook-sessions-list"]',
    title: 'Past Review Sessions',
    content:
      'All previous reviews run with this playbook appear here. See the status, document count, findings breakdown by severity, and overall risk score. Click any session to jump straight into the review workspace.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Table Column Item Tour - shown inside the columns editor when editing a template
// ---------------------------------------------------------------------------
export const tableColumnItemTourSteps: Step[] = [
  {
    target: '[data-tour-id="table-col-item"]',
    title: 'Column Card',
    content:
      'Each column card lets you configure the extraction. Set the column name, choose a data type (text, date, number, boolean, or currency), and write an AI query that tells the system exactly what to extract from each document.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="table-col-add-button"]',
    title: 'Add More Columns',
    content:
      'Click here to add additional columns. Each column becomes a field the AI extracts from every document in the batch. You can remove columns by hovering over a card and clicking the trash icon.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// TASKS
// ═══════════════════════════════════════════════════════════════════════════

// ---------------------------------------------------------------------------
// Task List Tour - shown on the main Tasks page
// ---------------------------------------------------------------------------
export const taskListTourSteps: Step[] = [
  {
    target: '[data-tour-id="task-list-header"]',
    title: 'Your Tasks',
    content:
      'This is your task management hub. Create, track, and organize tasks across matters. Use the "Add Task" button to get started.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="task-list-filters"]',
    title: 'Filter & Search',
    content:
      'Filter tasks by matter, status, or priority. Use the search bar to find tasks quickly, and toggle between grid and table views.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="task-list-table"]',
    title: 'Task Table',
    content:
      'View all your tasks with key details - name, linked matter, status, due date, priority, collaborators, and more. Click any row to open the full task detail.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Task Form Tour - shown when creating a new task
// ---------------------------------------------------------------------------
export const taskFormTourSteps: Step[] = [
  {
    target: '[data-tour-id="task-form-details"]',
    title: 'Task Details',
    content:
      'Fill in the task title, description, priority, status, due date, and optionally link it to a matter for better organization.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="task-form-collaborators"]',
    title: 'Collaborators & Files',
    content:
      'Add team members to collaborate on this task and attach any relevant files. Collaborators will be notified and can access the task.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Task Detail Tour - shown on the task detail page
// ---------------------------------------------------------------------------
export const taskDetailTourSteps: Step[] = [
  {
    target: '[data-tour-id="task-detail-badges"]',
    title: 'Task Status at a Glance',
    content:
      'See the current status, priority, and due date right in the header. These update automatically as the task progresses.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="task-detail-collaborators"]',
    title: 'Collaborators',
    content:
      'See who has access to this task. Press the faces to open the Access pane, where the full list lives and the "Manage access" button changes it.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="task-detail-tabs"]',
    title: 'Task Sections',
    content:
      'Switch between Details for the description and metadata, Feed for activity and comments, and Attachments for uploaded files.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Task Details Tab Tour - description, metadata, linked matter
// ---------------------------------------------------------------------------
export const taskDetailsTabTourSteps: Step[] = [
  {
    target: '[data-tour-id="task-description-card"]',
    title: 'Description',
    content:
      'View and edit the task description. Click "Edit" to update the title, description, due date, and priority.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="task-metadata-grid"]',
    title: 'Task Metadata',
    content:
      'Quick-view cards showing the current status, priority level, creation date, and due date. Overdue or upcoming deadlines are highlighted.',
    placement: 'top',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="task-linked-matter"]',
    title: 'Linked Matter',
    content:
      'Tasks can be linked to a matter for context. Click the matter name to navigate to it, or use the dropdown to link or change the associated matter.',
    placement: 'left',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Task Feed Tab Tour - comments and activity
// ---------------------------------------------------------------------------
export const taskFeedTabTourSteps: Step[] = [
  {
    target: '[data-tour-id="task-feed-list"]',
    title: 'Activity Feed',
    content:
      'All activity and comments for this task are shown here. Search through comments and see who posted each one.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="task-feed-input"]',
    title: 'Post a Comment',
    content:
      'Write a comment to update your team on progress, ask questions, or share notes about the task.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Task Attachments Tab Tour - file management
// ---------------------------------------------------------------------------
export const taskAttachmentsTabTourSteps: Step[] = [
  {
    target: '[data-tour-id="task-attachments-actions"]',
    title: 'Manage Attachments',
    content:
      'Upload files to attach them to this task. Use the search bar to find specific files quickly.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="task-attachments-table"]',
    title: 'File List',
    content:
      'All uploaded files appear here with their name, type, upload date, and who uploaded them. Click a row to preview, or use the actions menu to download or remove files.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Calendar Tour - main calendar page orientation
// ---------------------------------------------------------------------------
export const calendarTourSteps: Step[] = [
  {
    target: '[data-tour-id="calendar-header"]',
    title: 'Calendar Navigation',
    content:
      'Use Prev and Next to move between dates, or press Today to jump to the current date. Switch between Day, Week, Month, and Year views using the dropdown, and add new events with the Add Event button. You can also Sync, Export, or Import calendar events from the toolbar.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="calendar-legend"]',
    title: 'Event Type Legend',
    content:
      'Each color represents a different event type - tasks, matters, reminders, and custom events. Use this legend to quickly identify event categories at a glance.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="calendar-content"]',
    title: 'Your Schedule',
    content:
      'This is your main calendar view. Click on any event to see its details, or click on a day to drill into the day view. You can also drag events to reschedule them.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ===========================================================================
// MANAGEMENT - People / Departments / Teams
// ===========================================================================

// ---------------------------------------------------------------------------
// People List Tour
// ---------------------------------------------------------------------------
export const peopleListTourSteps: Step[] = [
  {
    target: '[data-tour-id="people-list-header"]',
    title: 'People Overview',
    content:
      'This is your People page. From the header you can add new people to your organization by sending email invitations.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="people-list-filters"]',
    title: 'Search & Filter',
    content:
      'Filter your people by department or role. Use the search bar to quickly find someone, and toggle between grid and table views.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="people-list-content"]',
    title: 'Your Team',
    content:
      'All members of your organization appear here - including their name, role, departments, teams, and join date. Click on a person to see their full profile.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// People Form Tour
// ---------------------------------------------------------------------------
export const peopleFormTourSteps: Step[] = [
  {
    target: '[data-tour-id="people-form"]',
    title: 'Invite People',
    content:
      'Enter email addresses to invite people to your organization. You can invite multiple people at once and assign each a role before sending.',
    placement: 'right',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// People Detail Tour
// ---------------------------------------------------------------------------
export const peopleDetailTourSteps: Step[] = [
  {
    target: '[data-tour-id="people-detail-header"]',
    title: 'Profile Overview',
    content:
      'This section shows the person\'s name, email, role, and availability status. If this is your own profile, you can edit it from the Account settings.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="people-detail-personal"]',
    title: 'Personal Information',
    content:
      'View personal details such as hourly rate, years of experience, working hours, and languages spoken.',
    placement: 'right',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="people-detail-professional"]',
    title: 'Professional Summary',
    content:
      'See the person\'s bio, specializations, and professional memberships at a glance.',
    placement: 'left',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Department List Tour
// ---------------------------------------------------------------------------
export const departmentListTourSteps: Step[] = [
  {
    target: '[data-tour-id="dept-list-header"]',
    title: 'Departments Overview',
    content:
      'Manage your organization\'s departments from here. Click "Add Department" to create a new one, or use the dropdown to access the Recycle Bin.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="dept-list-filters"]',
    title: 'Search & View Mode',
    content:
      'Search for departments by name or switch between grid and table views to find what you need.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="dept-list-content"]',
    title: 'Department Directory',
    content:
      'All your departments are listed here with their members, documents, and status. Click on a department to view its full details.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Department Form Tour
// ---------------------------------------------------------------------------
export const departmentFormTourSteps: Step[] = [
  {
    target: '[data-tour-id="dept-form"]',
    title: 'Create a Department',
    content:
      'Fill in the department name, description, practice areas, and assign initial members. You can designate a lead for the department.',
    placement: 'right',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Department Detail Tour
// ---------------------------------------------------------------------------
export const departmentDetailTourSteps: Step[] = [
  {
    target: '[data-tour-id="dept-detail-tabs"]',
    title: 'Department Sections',
    content:
      'Switch between Details, Feed, and Documents tabs to manage different aspects of this department.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="dept-detail-content"]',
    title: 'Tab Content',
    content:
      'The content for the selected tab appears here. In Details you can view and edit department info. In Feed you can post updates. In Documents you can upload and manage files.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Team List Tour
// ---------------------------------------------------------------------------
export const teamListTourSteps: Step[] = [
  {
    target: '[data-tour-id="team-list-header"]',
    title: 'Teams Overview',
    content:
      'Manage your organization\'s teams from here. Click "Add Team" to create a new one, or access the Recycle Bin from the dropdown.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="team-list-filters"]',
    title: 'Search & View Mode',
    content:
      'Search for teams by name or switch between grid and table views.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="team-list-content"]',
    title: 'Team Directory',
    content:
      'All your teams are listed here with their members, documents, and status. Click on a team to view its full details.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Team Form Tour
// ---------------------------------------------------------------------------
export const teamFormTourSteps: Step[] = [
  {
    target: '[data-tour-id="team-form"]',
    title: 'Create a Team',
    content:
      'Fill in the team name, description, practice areas, department, and assign team members. You can designate a team lead during creation.',
    placement: 'right',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Team Detail Tour
// ---------------------------------------------------------------------------
export const teamDetailTourSteps: Step[] = [
  {
    target: '[data-tour-id="team-detail-tabs"]',
    title: 'Team Sections',
    content:
      'Switch between Details, Feed, Matters, and Documents tabs to manage different aspects of this team.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="team-detail-content"]',
    title: 'Team Content',
    content:
      'The Details tab shows the team description, metadata, practice areas, and members. Use the Feed for team updates and Documents for shared files.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Settings Page Tour - orientation for the Settings tabs
// ---------------------------------------------------------------------------
export const settingsPageTourSteps: Step[] = [
  {
    target: '[data-tour-id="settings-tabs"]',
    title: 'Settings Navigation',
    content:
      'Use these tabs to configure your platform experience. General covers language, theme, and currency. AI lets you customize how the assistant behaves. Notifications controls your alert preferences.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="settings-cards"]',
    title: 'Quick Settings',
    content:
      'These cards let you change language, switch between light and dark themes, and set your preferred currency. Changes are saved automatically.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Account Settings Tour - orientation for Account tabs
// ---------------------------------------------------------------------------
export const accountSettingsTourSteps: Step[] = [
  {
    target: '[data-tour-id="account-tabs"]',
    title: 'Account Navigation',
    content:
      'Manage your personal profile, subscription plan, and activity log from these tabs. The Personal tab lets you update your name, photo, and contact info.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// AI Settings Tab Tour
// ---------------------------------------------------------------------------
export const aiSettingsTourSteps: Step[] = [
  {
    target: '[data-tour-id="ai-settings-cards"]',
    title: 'AI Preferences',
    content:
      'Configure how the AI assistant works for you. Set your legal jurisdiction, preferred language for AI responses, and choose a speaker voice for audio features.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Notification Settings Tab Tour
// ---------------------------------------------------------------------------
export const notificationSettingsTourSteps: Step[] = [
  {
    target: '[data-tour-id="notification-sections"]',
    title: 'Notification Categories',
    content:
      'Each card represents a feature area - Tasks, Calendar, Matters, and more. Expand any card to toggle individual notification types on or off. Some notifications have customizable reminder timings.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Organization Settings Tab Tour
// ---------------------------------------------------------------------------
export const orgSettingsTourSteps: Step[] = [
  {
    target: '[data-tour-id="org-retention"]',
    title: 'Recycle Bin Retention',
    content:
      'Set how many days deleted items stay in the recycle bin before being permanently removed. This applies to all users in your organization.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="org-tags"]',
    title: 'Document Tags',
    content:
      'Create color-coded tags to classify files and folders across your organization. Tags help everyone find and organize documents consistently.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Recycle Bin Tab Tour
// ---------------------------------------------------------------------------
export const recycleBinTourSteps: Step[] = [
  {
    target: '[data-tour-id="recycle-bin-area"]',
    title: 'Recycle Bin',
    content:
      'Deleted items are kept here before permanent removal. Use filters to find items by type, sort by expiry date, and restore or permanently delete items. The "Days Remaining" column shows when each item will be auto-deleted.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Personal Profile Tab Tour
// ---------------------------------------------------------------------------
export const personalTabTourSteps: Step[] = [
  {
    target: '[data-tour-id="personal-header"]',
    title: 'Your Profile',
    content:
      'Click your photo to upload a new profile picture. Your name, email, and role are displayed here.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="personal-form"]',
    title: 'Personal & Professional Details',
    content:
      'Update your personal information on the left - name, hourly rate, experience, and languages. The right side covers your professional summary, bio, and specializations. Remember to save your changes.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Activity / Audit Log Tab Tour
// ---------------------------------------------------------------------------
export const activityLogTourSteps: Step[] = [
  {
    target: '[data-tour-id="audit-log-area"]',
    title: 'Activity Log',
    content:
      'View a chronological record of actions performed on the platform. Filter by resource type or action, search for specific entries, and click any row to see full details.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Plans & Billing Tab Tour
// ---------------------------------------------------------------------------
export const plansBillingTourSteps: Step[] = [
  {
    target: '[data-tour-id="plans-current"]',
    title: 'Current Plan & Billing',
    content:
      'See your active subscription details on the left and manage payment methods and invoices on the right. You can upgrade, add seats, or switch billing periods from here.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Help Center Tour
// ---------------------------------------------------------------------------
export const helpCenterTourSteps: Step[] = [
  {
    target: '[data-tour-id="help-tabs"]',
    title: 'Help Center',
    content:
      'Use the Support tab to create and track support tickets, or switch to Feedback to share suggestions and ideas with us.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Support Center Tour
// ---------------------------------------------------------------------------
export const supportCenterTourSteps: Step[] = [
  {
    target: '[data-tour-id="support-tickets"]',
    title: 'Support Tickets',
    content:
      'View all your support tickets here. Click any row for details, or use the "Create Support Ticket" button to report a new issue. You can sort and filter to find specific tickets.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Feedback Tour
// ---------------------------------------------------------------------------
export const feedbackTourSteps: Step[] = [
  {
    target: '[data-tour-id="feedback-form"]',
    title: 'Share Feedback',
    content:
      'Type your thoughts on usability, features, or any area you\'d like us to improve. You have up to 500 words - click Send when ready.',
    placement: 'right',
    disableBeacon: true,
  },
];

// ---------------------------------------------------------------------------
// Notifications Sidepanel Tour
// ---------------------------------------------------------------------------
export const notificationsPanelTourSteps: Step[] = [
  {
    target: '[data-tour-id="notifications-panel"]',
    title: 'Notifications',
    content:
      'Your notification center. New activity appears in the Unread section at the top. Click any notification to navigate to the related item. Use "Mark all as read" to clear them.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

// ---------- Support Ticket Form ----------

export const supportTicketFormTourSteps: Step[] = [
  {
    target: '[data-tour-id="support-ticket-form"]',
    title: 'Create a Support Ticket',
    content:
      'Use this form to submit a new support ticket. Fill in a clear title and a detailed description of your issue, select a priority level, and optionally attach files. Hit "Submit Ticket" when you\'re done.',
    placement: 'right',
    disableBeacon: true,
  },
];

// ---------- Ticket Details ----------

export const ticketDetailsTourSteps: Step[] = [
  {
    target: '[data-tour-id="ticket-details-left"]',
    title: 'Ticket Overview',
    content:
      'This panel shows the full details of your ticket - including its ID, title, current status, priority, and timestamps for when it was created and last updated.',
    placement: 'right',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="ticket-description"]',
    title: 'Description',
    content:
      'The full description of the issue you reported. Long descriptions are scrollable so nothing gets cut off.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="ticket-attachments"]',
    title: 'Attachments',
    content:
      'Any files or screenshots you attached when creating the ticket. Click images to preview them full-size, or use the download button to save any attachment.',
    placement: 'top',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="ticket-comments"]',
    title: 'Comments',
    content:
      'Use this section to communicate with the support team. Type your message and click "Post" to add a comment. All conversation history is shown below.',
    placement: 'left',
    disableBeacon: true,
  },
];

// ---------- Files List ----------

export const filesListTourSteps: Step[] = [
  {
    target: '[data-tour-id="files-toolbar"]',
    title: 'Search, Sort & Actions',
    content:
      'Use this toolbar to search files by name, sort by name or date, and access bulk actions like deleting selected items or opening the Recycle Bin.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="files-table"]',
    title: 'Your Files & Folders',
    content:
      'Browse your file system here. Folders are shown with an icon - click one to navigate inside. Files can be clicked to open the document viewer. Use the checkbox column to select items for bulk operations, and the actions menu (⋮) on each row to rename, manage access, view activity, or delete.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ---------- Create Folder Form ----------

export const createFolderFormTourSteps: Step[] = [
  {
    target: '[data-tour-id="create-folder-form"]',
    title: 'Create a New Folder',
    content:
      'Give your folder a name, optionally choose a parent folder to nest it under, add tags for organisation, and invite collaborators who should have access. Click "Create Folder" when ready.',
    placement: 'right',
    disableBeacon: true,
  },
];

// ---------- Import Files Form ----------

export const importFilesFormTourSteps: Step[] = [
  {
    target: '[data-tour-id="import-files-form"]',
    title: 'Import Files',
    content:
      'Choose whether to upload into an existing folder or create a new one. Select a folder path, optionally add tags and collaborators, then drag and drop your files or click to browse. Hit "Import Files" to start the upload.',
    placement: 'right',
    disableBeacon: true,
  },
];

// ---------- File Viewer / Document Editor ----------

export const fileViewerTourSteps: Step[] = [
  {
    target: '[data-tour-id="file-navigator"]',
    title: 'File Navigator',
    content:
      'Quickly switch between files in the current folder without going back. You can also navigate into sub-folders. File metadata like type, size, and dates are shown here.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="file-content-area"]',
    title: 'Document Viewer',
    content:
      'Your document is displayed here. Word documents and text files can be edited inline. PDFs are rendered for viewing. Excel files open in a spreadsheet editor. Use the header actions to download, view version history, or open in Microsoft 365.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Tour: Dashboard Orientation
 * Shown on first visit to /home. Walks through all major dashboard sections.
 */
export const dashboardOrientationSteps: Step[] = [
  {
    target: '[data-tour-id="dashboard-greeting"]',
    title: 'Welcome to Your Dashboard',
    content: 'This is your home base. Everything you need is organized here - your projects, matters, tasks, and tools.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="dashboard-metrics"]',
    title: 'Quick Metrics',
    content: 'See your key numbers at a glance - open tasks, active matters, projects, and reviews. Click any card to jump to that section.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    /*
      One stop, not three. This used to point at three separate dashboard
      widgets - Your Projects, Recently Worked On, Upcoming Tasks - which were
      removed as duplicates of the three columns inside this single card. A
      Joyride step whose target does not exist is a step that silently breaks
      the tour, so they collapse here rather than lingering.
    */
    target: '[data-tour-id="dashboard-work"]',
    title: 'Your Work',
    content: 'Three columns, side by side: project deadlines, tasks due or overdue, and the matters you touched last. Complete a task without leaving the page, or start something new from the bottom of any column.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="dashboard-tools"]',
    title: 'AI Tools',
    content: 'Quick access to all your AI-powered tools - research, document review, due diligence tables, and more.',
    placement: 'top',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="dashboard-activity"]',
    title: 'Recent Activity',
    content: 'Track everything that happens across your workspace. Click "View All" to see your complete activity history.',
    placement: 'top',
    disableBeacon: true,
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// PROJECTS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Tour: Projects List Orientation
 * Shown on first visit to /projects.
 */
export const projectsListOrientationSteps: Step[] = [
  {
    target: '[data-tour-id="projects-add-button"]',
    title: 'Create a Project',
    content: 'Start by creating a project to organize related matters, tasks, and resources under one roof.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="projects-list"]',
    title: 'Your Projects',
    content: 'All your projects appear here. Use the filters and search to find what you need. Click any project to see its details, linked resources, and team members.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

/**
 * Tour: Project Detail Orientation
 * Shown on first visit to a project detail page.
 */
export const projectDetailOrientationSteps: Step[] = [
  {
    target: '[data-tour-id="project-header"]',
    title: 'Project Overview',
    content: 'This is your project hub. See the status, priority, due date, and team members at a glance.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="project-link-resource"]',
    title: 'Link Resources',
    content: 'Connect matters, tasks, reviews, and other resources to this project. Linking organizes them - it does not automatically share access.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="project-tabs"]',
    title: 'Details & Resources',
    content: 'Switch between project details and linked resources. The Resources tab shows everything connected to this project.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="project-actions-menu"]',
    title: 'Project Actions',
    content: 'Edit project details, view the activity log, or delete the project from this menu. Team access has its own pane - use the Access toggle on the pane rail.',
    placement: 'bottom',
    disableBeacon: true,
  },
];

/**
 * Tour: Contacts List
 * Highlights the toolbar and contacts table on the /contacts page.
 */
export const clientsListTourSteps: Step[] = [
  {
    target: '[data-tour-id="clients-toolbar"]',
    title: 'Filters & Actions',
    content:
      'Filter contacts by type, search by name, and access bulk actions here. When you select contacts, additional options like Deactivate and Delete appear. You can also access the Recycle Bin to restore previously deleted contacts.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="clients-table"]',
    title: 'Contact List',
    content:
      'All your contacts are listed here - clients, opposing parties, external counsel and anyone else you work with. The entity and its contact person are separate columns, Type is colour-coded by family, and Access shows who else can see the record. Click a row to open it.',
    placement: 'top',
    disableBeacon: true,
  },
];

/**
 * Tour: Contact Form
 * Shown when the contact creation form is open.
 */
export const clientFormTourSteps: Step[] = [
  {
    target: '[data-tour-id="client-form"]',
    title: 'Create a New Contact',
    content:
      'Start by selecting the contact type - the form adapts to individual or organization contacts. Fill in the required fields, optionally add collaborators and file attachments, then click Create Contact. For organizations you can also specify a contact person.',
    placement: 'right',
    disableBeacon: true,
  },
];

/**
 * Tour: Contact Details
 * Walks the user through the contact detail page including the Actions menu and tabs.
 */
export const clientDetailsTourSteps: Step[] = [
  {
    target: '[data-tour-id="client-actions-menu"]',
    title: 'Contact Actions',
    content:
      'This menu is your command center for managing this contact. From here you can edit its details, connect or create matters, upload documents, deactivate or reactivate the contact, view the activity log, or permanently delete it.',
    placement: 'bottom-end',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="client-tabs"]',
    title: 'Navigation Tabs',
    content:
      'Switch between tabs to explore different aspects of this contact. Details shows its contact information, record facts and recent activity. Matters lists all connected matters. Documents holds uploaded files.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="client-details-tab"]',
    title: 'Details Tab',
    content:
      'Read the contact\'s information, notes and record facts. Edit details opens a side panel to change them, and the Access pane shows who is on this contact - you can add or remove team members and set a lead person.',
    placement: 'top',
    disableBeacon: true,
  },
];
